/**
 * iter86 - Notification Bell + panel for the top nav.
 *
 * Founder priority: Manifest is the highest-loved feature, and the loop
 * that makes it get USED is notifications. This bell is the pull-back.
 *
 * Behavior:
 *   - Polls /api/notifications/unread-count every ~20s while the tab is
 *     visible. Pauses when hidden (visibilitychange) so we don't hammer
 *     the backend while a student is away.
 *   - Badge only renders when the real unread count > 0. Zero means no
 *     dot - no fake bait, per the founder's honesty rule.
 *   - Click the bell -> fetches the full recent list, opens a small
 *     panel. Click a row -> marks that notification read + navigates to
 *     its link. "Mark all read" also available.
 *   - Uses the platform's mint/light design system, not a Messenger
 *     visual clone - only the functional pattern is copied.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";
import { playNotificationCue, safeVibrate } from "@/lib/notificationCue";

const POLL_MS = 20000;

export default function NotificationBell() {
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen]   = useState(false);
  const [items, setItems] = useState(null);   // null | []
  const [busy,  setBusy]  = useState(false);
  const panelRef = useRef(null);
  const btnRef   = useRef(null);
  // Item W - first-mount guard for the cue. Chime only when the unread
  // count strictly INCREASES from a previously-known value. This ref is
  // null until the first successful poll lands, at which point we seed
  // it and never chime on that baseline.
  const lastCountRef = useRef(null);
  const soundEnabledRef = useRef(true);   // updated from /preferences on mount

  const refreshCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      const next = r.data?.unread_count || 0;
      // Item W - fire the sound + vibration cue when the count increases
      // past a known baseline. Never on first mount, never when the count
      // stays flat or drops.
      const prev = lastCountRef.current;
      if (prev !== null && next > prev) {
        // Item W - honour the sound toggle for BOTH the audio cue AND the
        // vibration. "Notification sound off" is honest silent mode.
        if (soundEnabledRef.current) {
          playNotificationCue();
          safeVibrate(40);
        }
      }
      lastCountRef.current = next;
      setCount(next);
    } catch {
      // Silent - the bell should not spam errors during background poll.
    }
  }, []);

  const loadList = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/notifications?limit=30");
      setItems(r.data?.notifications || []);
      setCount(r.data?.unread_count || 0);
    } catch {
      setItems([]);
    } finally { setBusy(false); }
  }, []);

  // Initial + poll loop.
  useEffect(() => {
    // Item W - read the user's persisted sound preference once. Failure is
    // silent; we default to enabled (matches messaging-app expectations).
    api.get("/notifications/preferences")
      .then(r => { soundEnabledRef.current = r.data?.sound_enabled !== false; })
      .catch(() => {});
    // Item W - allow the settings toggle to update the ref live without a
    // full remount. `notification-prefs-updated` is dispatched by the
    // NotificationPrefsToggle component on save.
    const onPrefs = (e) => {
      if (typeof e?.detail?.sound_enabled === "boolean") {
        soundEnabledRef.current = e.detail.sound_enabled;
      }
    };
    window.addEventListener("notification-prefs-updated", onPrefs);

    refreshCount();
    let interval;
    const start = () => {
      stop();
      interval = setInterval(() => {
        if (document.visibilityState === "visible") refreshCount();
      }, POLL_MS);
    };
    const stop = () => { if (interval) clearInterval(interval); interval = null; };
    start();
    const onVis = () => {
      if (document.visibilityState === "visible") { refreshCount(); start(); }
      else stop();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("notification-prefs-updated", onPrefs);
    };
  }, [refreshCount]);

  // Click outside -> close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target))   return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openPanel = async () => {
    setOpen(true);
    await loadList();
  };

  const clickItem = async (n) => {
    setOpen(false);
    // Optimistic mark-read on click.
    if (!n.read_at) {
      try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch {}
      setCount(c => Math.max(0, c - 1));
    }
    if (n.link) nav(n.link);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { all: true });
      setCount(0);
      setItems(rows => (rows || []).map(r => ({ ...r, read_at: new Date().toISOString() })));
    } catch {}
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="notif-bell"
        aria-label={count > 0 ? `${count} unread notifications` : "Notifications"}
        onClick={open ? () => setOpen(false) : openPanel}
        className="relative moe-btn moe-btn-ghost !py-2 !px-3"
      >
        <Bell size={16} strokeWidth={2.2} />
        {count > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--moe-pink)] text-white text-[10px] font-bold flex items-center justify-center tabular-nums"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notif-panel"
          className={
            // iter92 (Item FF) — Responsive panel positioning.
            // Mobile/tablet (<lg): the bell sits inside a compact right-aligned
            // slot next to the hamburger, so an `absolute right-0 w-[380px]`
            // dropdown either overflows the left of the viewport (measured:
            // -32px at 375px) or clips against the burger. Solution: on
            // mobile, escape the local anchor entirely with `fixed inset-x-2`
            // so the panel becomes near-full-width with 8px margins and can
            // never clip regardless of where in the nav the bell sits.
            // Desktop (lg+): revert to the original right-anchored dropdown.
            "moe-card p-0 z-50 overflow-hidden bg-paper shadow-xl " +
            "fixed left-2 right-2 top-[64px] w-auto max-w-none " +
            "lg:absolute lg:left-auto lg:right-0 lg:top-[calc(100%+8px)] " +
            "lg:w-[380px] lg:max-w-[92vw]"
          }
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink/10">
            <div className="text-[11px] uppercase tracking-widest text-elec font-bold">
              Notifications
            </div>
            {count > 0 && (
              <button
                data-testid="notif-mark-all"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted hover:text-ink"
              >
                <Check size={11}/> Mark all read
              </button>
            )}
          </div>

          {items === null || busy ? (
            <div className="p-6 text-center text-sm text-muted italic">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center" data-testid="notif-empty">
              <div className="mx-auto w-10 h-10 rounded-full bg-ink/5 flex items-center justify-center mb-3">
                <Bell size={16} className="text-muted"/>
              </div>
              <div className="text-sm font-semibold text-ink">You're all caught up.</div>
              <div className="mt-1 text-[12px] text-muted leading-snug max-w-[220px] mx-auto">
                Nothing new right now. Messages, Assembly updates, and match responses land here.
              </div>
            </div>
          ) : (
            <ul className="max-h-[420px] overflow-auto divide-y divide-ink/10">
              {items.map(n => (
                <li key={n.id}>
                  <button
                    data-testid={`notif-row-${n.type}`}
                    onClick={() => clickItem(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-ink/5 transition-colors flex gap-3 ${
                      n.read_at ? "" : "bg-[color:var(--moe-mint)]/25"
                    }`}
                  >
                    <span
                      className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${
                        n.read_at ? "bg-transparent" : "bg-elec"
                      }`}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-ink truncate">
                        {n.title}
                      </div>
                      {n.body && (
                        <div className="mt-0.5 text-[12px] text-ink/70 line-clamp-2 leading-snug">
                          {n.body}
                        </div>
                      )}
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-muted font-bold">
                        {relTime(n.created_at)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(1, Math.floor((now - then) / 1000));
    if (s < 60)     return `${s}s ago`;
    if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
    if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}
